import PropTypes from 'prop-types';
import { DndProvider } from 'react-dnd-cjs';
import HTML5Backend from 'react-dnd-html5-backend-cjs';
import { Route } from 'react-router-dom';

import { instrumentTypes } from '@gef-ui/apm/esm/instruments';
import GEFBootstrap, { globalInit } from '@gef-ui/bootstrap-redux';
import GefInitDelegations from '@gef-ui/delegations/components/InitDelegations';
import ReduxToastr from '@gef-ui/toastr/containers/ReduxToastr';

import DelegationTab from 'app/components/DelegationTab';
import Error from 'app/components/Error';
import IascError from 'app/components/IascError';
import InitDelegations from 'app/components/InitDelegations';
import Logout from 'app/components/Logout';
import MigrationInProgress from 'app/components/MigrationInProgress';
import NoAccess from 'app/components/NoAccess';
import Configurator from 'app/configurator';
import {
	fetchUserInfo,
	fetchUserInfoWithRepresentedUser,
	loadApplication,
} from 'app/configurator/actions/configurator';
import { FETCH_REPRESENTED_USER_INFO_PATH, FETCH_USER_INFO_PATH } from 'app/configurator/constants';
import contextDefaults from 'app/contextDefaults';
import reducers from 'app/store';
import initialState from 'app/store/initialState';
import middlewares from 'app/store/middlewares';
import fetch from 'features/common/fetch';
import { getIsLoaded, getMetaData } from 'features/common/fetch/selectors';
import localization from 'localization';
import { getToastrConfig } from 'utils';
import history from 'utils/history';

import Routers from '../routers';
import theme from '../theme';
import { breadcrumbsOptions } from './Apm/utils';
import App from './App';
import { CustomRouter } from './CustomRouter';
import ScrollToTop from './ScrollToTop';
import UserRole from './UserRole';

const { DOM, REDUX, APM_TRANSACTION, XHR, FETCH, CONSOLE } = instrumentTypes;

globalInit({ localization });

const postProcessor = ({ urls, ...config }) => ({
	...config,
	urls: {
		...urls,
		baseUrl: config.apiUrl,
	},
});

const Root = () => (
	<CustomRouter history={history}>
		<GEFBootstrap
			apmProps={{
				enabled: true,
				breadcrumbsInstruments: [DOM, REDUX, APM_TRANSACTION, XHR, FETCH, CONSOLE],
				breadcrumbsOptions,
			}}
			authorizationProps={{ persistLocationState: true, waitForFullAuthProcess: true }}
			configJSONProps={{ postProcessor }}
			defaults={contextDefaults}
			notistackProps={{ anchorOrigin: { vertical: 'top', horizontal: 'right' } }}
			reduxProps={{ initialState, middlewares, reducers }}
			theme={theme}
			themeProviderProps={{ defaultMode: 'light' }}
		>
			<InitDelegations>
				<GefInitDelegations
					continueAction={loadApplication}
					toastrConfig={getToastrConfig('common:copiedToClipboard')}
					wrappers={{
						delegations: DelegationTab,
						error: Error,
						noAccess: NoAccess,
						logout: Logout,
					}}
					selectors={{
						getIsLogoutFetchLoaded: getIsLoaded(fetch.constants.urls.clearUsersCache),
						getIsOriginalUserFetchLoaded: getIsLoaded(FETCH_USER_INFO_PATH),
						getCalledUserInfoCompany: getMetaData(FETCH_REPRESENTED_USER_INFO_PATH),
					}}
					userInfoEndpointCreators={{
						createFetchAction: fetchUserInfo,
						createFetchActionWithRepresentedUser: fetchUserInfoWithRepresentedUser,
					}}
				>
					<Route path="/ias-error" element={<IascError />} />
					<Route path="/migration-in-progress" element={<MigrationInProgress />} />
					<Route
						path="/*"
						element={
							<Configurator>
								<DndProvider backend={HTML5Backend}>
									<UserRole>
										<ScrollToTop>
											<App>
												<Routers />
											</App>
										</ScrollToTop>
									</UserRole>
								</DndProvider>
							</Configurator>
						}
					/>
				</GefInitDelegations>
				{/* still used because of ErrorComponent from InitDelegations - copyToClipBoard feature */}
				<ReduxToastr />
			</InitDelegations>
		</GEFBootstrap>
	</CustomRouter>
);

Root.propTypes = {
	store: PropTypes.any,
};

export default Root;
